import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import logo from '../assets/brand/naija-property-logo.svg';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCurrency, SUPPORTED_CURRENCIES } from '../context/CurrencyContext';
import {
  KeyIcon,
  DollarIcon,
  CalendarIcon,
  SunIcon,
  MoonIcon,
  ChevronDownIcon,
  PlusIcon,
} from './IconSet';
import HeaderNotificationBell from './HeaderNotificationBell';

const PRIMARY_LISTING_TYPES = [
  { key: 'buy', label: 'For Sale', Icon: DollarIcon },
  { key: 'short-let', label: 'Short-let', Icon: CalendarIcon },
  { key: 'rent', label: 'For Rent', Icon: KeyIcon },
];

const RESOURCE_LINKS = [
  { key: 'guides', label: 'Property Guide', path: '/blog' },
  { key: 'safety', label: 'Safety Tips', path: '/safety' },
  { key: 'help', label: 'Help', path: '/help' },
];

function MoreResourcesMenu({ active, onSelect }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="more-browse-menu" ref={menuRef}>
      <button
        type="button"
        className={`nav-btn nav-btn--subtle${active ? ' active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        Resources <ChevronDownIcon size={13} />
      </button>
      {open && (
        <div className="more-browse-menu__dropdown" role="menu">
          {RESOURCE_LINKS.map((link) => (
            <button
              key={link.key}
              type="button"
              className="more-browse-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSelect(link.path);
              }}
            >
              {link.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

MoreResourcesMenu.propTypes = {
  active: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

function CurrencyMenu() {
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="currency-menu" ref={menuRef}>
      <button
        type="button"
        className="currency-menu__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Select display currency"
      >
        {currency} <ChevronDownIcon size={13} />
      </button>
      {open && (
        <div className="currency-menu__dropdown" role="menu">
          {SUPPORTED_CURRENCIES.map(({ code, symbol, label }) => (
            <button
              key={code}
              type="button"
              className={`currency-menu__item${code === currency ? ' active' : ''}`}
              role="menuitem"
              onClick={() => {
                setCurrency(code);
                setOpen(false);
              }}
            >
              <span className="currency-menu__symbol">{symbol}</span>
              <span className="currency-menu__code">{code}</span>
              {code === currency ? (
                <span className="currency-menu__check" aria-hidden="true">
                  ✓
                </span>
              ) : (
                <span className="currency-menu__label">{label}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, onNavigate, onLogout, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const menuRef = useRef(null);
  const initials = user.name
    ? user.name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    : '?';

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    setAvatarLoadError(false);
  }, [user.avatar_url]);

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        type="button"
        className="user-menu__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Account menu for ${user.name}`}
      >
        <span className="user-menu__avatar" aria-hidden="true">
          {user.avatar_url && !avatarLoadError ? (
            <img
              src={user.avatar_url}
              alt=""
              className="user-menu__avatar-image"
              onError={() => setAvatarLoadError(true)}
            />
          ) : (
            initials
          )}
        </span>
        <span className="user-menu__name">{user.name.split(' ')[0]}</span>
      </button>

      {open && (
        <div className="user-menu__dropdown" role="menu">
          <div className="user-menu__info">
            <span className="user-menu__full-name">{user.name}</span>
            <span className="user-menu__role">{user.role}</span>
          </div>
          <hr className="user-menu__divider" />
          <button
            type="button"
            className="user-menu__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate('/favorites');
            }}
          >
            ❤️ Favourites
          </button>
          <button
            type="button"
            className="user-menu__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate('/messages');
            }}
          >
            💬 Messages
          </button>
          <button
            type="button"
            className="user-menu__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate('/settings');
            }}
          >
            ⚙️ Settings
          </button>
          <button
            type="button"
            className="user-menu__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate('/agent');
            }}
          >
            📊 Dashboard
          </button>
          {isAdmin && (
            <button
              type="button"
              className="user-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onNavigate('/admin');
              }}
            >
              ⚙️ Admin
            </button>
          )}
          <button
            type="button"
            className="user-menu__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate('/security');
            }}
          >
            {user.twoFactorEnabled ? '🔐 2FA enabled' : '🔓 Enable 2FA'}
          </button>
          <button
            type="button"
            className="user-menu__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
              onNavigate('/');
            }}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

UserMenu.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    role: PropTypes.string,
    twoFactorEnabled: PropTypes.bool,
    avatar_url: PropTypes.string,
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
};

function SiteHeader({
  activePage,
  onNavigate,
  filterType,
  setFilterType,
  setFilterPropertyType,
}) {
  const { user, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  const handleListingTypeClick = (type) => {
    setFilterType(type);
    setFilterPropertyType('');
    onNavigate(`/browse?type=${encodeURIComponent(type)}`);
  };

  const isResourcesActive = ['safety', 'blog'].includes(activePage);

  return (
    <header className="site-header">
      <button
        type="button"
        className="brand-btn"
        onClick={() => onNavigate('/')}
        aria-label="NaijaNest home"
      >
        <img src={logo} alt="NaijaNest logo" className="brand-logo" />
        <span className="brand-fallback">NaijaNest</span>
      </button>

      <nav className="site-nav">
        {PRIMARY_LISTING_TYPES.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            className={`nav-btn${activePage === 'browse' && filterType === key ? ' active' : ''}`}
            onClick={() => handleListingTypeClick(key)}
          >
            <Icon size={15} /> {label}
          </button>
        ))}

        <button
          type="button"
          className={`nav-btn${activePage === 'list-property' ? ' active' : ''}`}
          onClick={() => onNavigate('/list-property')}
        >
          <PlusIcon size={15} /> List a Property
        </button>

        <MoreResourcesMenu active={isResourcesActive} onSelect={onNavigate} />
      </nav>

      <div className="site-header__auth">
        <CurrencyMenu />
        <button
          type="button"
          className="theme-toggle-btn"
          aria-pressed={isDarkMode}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleTheme}
        >
          {isDarkMode ? <SunIcon size={16} /> : <MoonIcon size={16} />}
        </button>
        {user && <HeaderNotificationBell />}
        {user ? (
          <UserMenu user={user} onNavigate={onNavigate} onLogout={logout} isAdmin={isAdmin} />
        ) : (
          <button
            type="button"
            className={`header-login-btn${activePage === 'login' ? ' active' : ''}`}
            onClick={() => onNavigate('/login')}
          >
            Log in
          </button>
        )}
      </div>
    </header>
  );
}

export default SiteHeader;

SiteHeader.propTypes = {
  activePage: PropTypes.string,
  onNavigate: PropTypes.func.isRequired,
  filterType: PropTypes.string,
  setFilterType: PropTypes.func.isRequired,
  setFilterPropertyType: PropTypes.func.isRequired,
};
